import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { FileZip } from "@phosphor-icons/react";
import {
  DropZoneText,
  DropZoneTitle,
  EfolderDropZone,
  EfolderHeadRow,
  EfolderHeader,
  EfolderTable,
  EfolderTitle,
  EfolderWrapper,
  ErrorNotice,
  MutedText,
  SmallBtn,
  TabBadge,
} from "../MailIntakeStyled";
import EfolderRow from "./EfolderRow";
import EfolderConfirmModal from "./EfolderConfirmModal";
import {
  activeBatches,
  buildRows,
  cancelZip,
  isZip,
  makeZipEntry,
  newBatchId,
  pollIntervalFor,
  runZipUploads,
} from "../efolderQueue";
import {
  confirmAutoMatchedBatch,
  conflictPayload,
  errorMessage,
  listEfolderJobs,
  retryEfolderIdentify,
  skipEfolderJob,
} from "../../../service/efolderUpload";

const ELAPSED_TICK_MS = 60 * 1000;

const initialState = {
  entries: [],
  version: 0,
  jobs: {},
  batchIds: [],
  batchStartedAt: {},
  rejected: 0,
  loading: true,
  loadError: null,
  modal: null,
  notice: null,
};

const mergeJobs = (jobs, payloads) => {
  const next = { ...jobs };
  (payloads || []).forEach((job) => {
    if (job?.job_id != null) next[job.job_id] = job;
  });
  return next;
};

export const reducer = (state, action) => {
  switch (action.type) {
    case "entries/add":
      return {
        ...state,
        entries: [...state.entries, ...action.entries],
        batchIds: [action.batchId, ...state.batchIds],
        batchStartedAt: { ...state.batchStartedAt, [action.batchId]: action.now },
        rejected: action.rejected,
        version: state.version + 1,
      };
    case "entries/changed":
      return { ...state, version: state.version + 1 };
    case "rejected":
      return { ...state, rejected: action.rejected };
    case "jobs/merge":
      return { ...state, jobs: mergeJobs(state.jobs, action.jobs), loading: false, loadError: null };
    case "job/set":
      return { ...state, jobs: mergeJobs(state.jobs, [action.job]) };
    case "load/error":
      return { ...state, loading: false, loadError: action.error };
    case "modal/open":
      return { ...state, modal: { job: action.job, mode: action.mode } };
    case "modal/close":
      return { ...state, modal: null };
    case "notice":
      return { ...state, notice: action.notice };
    default:
      return state;
  }
};

const plural = (count, word) => `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;

/**
 * Central "eFolder Upload" tab (spec #2743 section 9): drop any number of
 * eFolder Express zips with no matter chosen; the backend works out whose
 * they are, and the rows here show each zip from upload through AI
 * processing, with review actions for the ones it could not decide.
 *
 * Own state (useReducer); shares only the tab shell with the mail queue.
 * Matter names and zip names are personal data: nothing here logs them.
 */
const EfolderUploadView = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [dragActive, setDragActive] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const inputRef = useRef(null);
  const entriesRef = useRef(state.entries);
  entriesRef.current = state.entries;

  const onEntryChange = useCallback((entry) => {
    if (entry.status === "sent" && entry.job) dispatch({ type: "job/set", job: entry.job });
    dispatch({ type: "entries/changed" });
  }, []);

  const loadList = useCallback(async () => {
    try {
      const body = await listEfolderJobs();
      dispatch({ type: "jobs/merge", jobs: body?.results });
    } catch (error) {
      dispatch({ type: "load/error", error: errorMessage(error, "Could not load eFolder uploads.") });
    }
  }, []);

  // A reload resumes from the list endpoint (the caller's recent jobs plus
  // everything waiting for review).
  useEffect(() => {
    loadList();
  }, [loadList]);

  // Entries are mutated in place by the queue; `version` is their change counter.
  const { entries, jobs, version } = state;
  const rows = useMemo(() => buildRows(entries, jobs, version), [entries, jobs, version]);
  const active = useMemo(() => activeBatches(state.batchIds, rows), [state.batchIds, rows]);
  const activeKey = active.join(",");

  // Poll each live batch (normally one) while any of its zips is still
  // moving; stop when every row is terminal.
  useEffect(() => {
    if (!activeKey) return undefined;
    const batches = activeKey.split(",");
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      await Promise.all(
        batches.map((batch) =>
          listEfolderJobs({ batch })
            .then((body) => {
              if (!cancelled) dispatch({ type: "jobs/merge", jobs: body?.results });
            })
            .catch(() => {
              /* a missed tick is retried on the next one */
            })
        )
      );
      if (cancelled) return;
      const oldest = Math.min(...batches.map((b) => state.batchStartedAt[b] || Date.now()));
      timer = setTimeout(tick, pollIntervalFor(oldest));
    };
    const oldest = Math.min(...batches.map((b) => state.batchStartedAt[b] || Date.now()));
    timer = setTimeout(tick, pollIntervalFor(oldest));
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeKey, state.batchStartedAt]);

  const uploading = rows.some((row) => row.stage === "uploading");
  const anyProcessing = rows.some((row) => row.stage === "processing");

  // Leaving the page kills an upload that is still leaving the browser.
  useEffect(() => {
    if (!uploading) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  // The "AI processing · 42 min" timer moves once a minute.
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const timer = setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
    return () => clearInterval(timer);
  }, [anyProcessing]);

  const handleFiles = useCallback(
    (fileList) => {
      const all = Array.from(fileList || []);
      const zips = all.filter(isZip);
      const rejected = all.length - zips.length;
      if (zips.length === 0) {
        dispatch({ type: "rejected", rejected });
        return;
      }
      const batchId = newBatchId();
      const busyKeys = new Set(
        entriesRef.current
          .filter((e) => e.status === "queued" || e.status === "uploading" || e.status === "sent")
          .map((e) => e.key)
      );
      const fresh = [];
      zips.forEach((file) => {
        const entry = makeZipEntry(file, batchId);
        if (!busyKeys.has(entry.key)) {
          busyKeys.add(entry.key);
          fresh.push(entry);
        }
      });
      dispatch({ type: "entries/add", entries: fresh, batchId, rejected, now: Date.now() });
      runZipUploads(fresh, { onChange: onEntryChange });
    },
    [onEntryChange]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = "";
  };

  const setJobFromResult = (payload) => dispatch({ type: "job/set", job: payload });

  // A 409 means someone else acted first; the body is the row as it is now.
  const withConflict = async (fn) => {
    try {
      setJobFromResult(await fn());
    } catch (error) {
      const current = conflictPayload(error);
      if (!current) throw error;
      setJobFromResult(current);
      dispatch({ type: "notice", notice: errorMessage(error, "Someone else already handled that zip.") });
    }
  };

  const handleSkip = (job, { reason, note }) =>
    withConflict(() => skipEfolderJob(job.job_id, { reason, note }));
  const handleRetryIdentify = (job) => withConflict(() => retryEfolderIdentify(job.job_id));

  const handleConfirmAll = async (batchId) => {
    try {
      const body = await confirmAutoMatchedBatch(batchId);
      dispatch({ type: "jobs/merge", jobs: body?.results });
      const confirmed = body?.confirmed?.length || 0;
      dispatch({
        type: "notice",
        notice: confirmed ? `Started ${plural(confirmed, "auto-matched zip")}.` : "Nothing left to confirm in that drop.",
      });
    } catch (error) {
      dispatch({ type: "notice", notice: errorMessage(error) });
    }
  };

  const needsReview = rows.filter((row) => row.stage === "needs_review");
  // Auto-matched zips parked because EFOLDER_AUTO_CONFIRM is off server-side.
  const autoWaiting = needsReview.filter((row) => row.job?.match?.status === "auto_matched");
  const autoWaitingBatches = Array.from(new Set(autoWaiting.map((row) => row.batchId).filter(Boolean)));
  const sessionRows = rows.filter((row) => row.session);
  const otherRows = rows.filter((row) => !row.session);

  const rowProps = {
    now,
    onCancel: (entry) => cancelZip(entry, { onChange: onEntryChange }),
    onRetryUpload: (entry) => runZipUploads([entry], { onChange: onEntryChange }),
    onConfirm: (job) => dispatch({ type: "modal/open", job, mode: "confirm" }),
    onReassign: (job) => dispatch({ type: "modal/open", job, mode: "reassign" }),
    onSkip: handleSkip,
    onRetryIdentify: handleRetryIdentify,
  };

  const renderTable = (list, testId) => (
    <EfolderTable data-testid={testId} role="table">
      <EfolderHeadRow role="row">
        <span>Zip</span>
        <span>Stage</span>
        <span>Match</span>
        <span>New · on file · failed</span>
        <span style={{ textAlign: "right" }}>Actions</span>
      </EfolderHeadRow>
      {list.map((row) => (
        <EfolderRow key={row.key} row={row} {...rowProps} />
      ))}
    </EfolderTable>
  );

  return (
    <EfolderWrapper>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        multiple
        style={{ display: "none" }}
        onChange={handleInputChange}
        data-testid="efolder-upload-input"
      />

      <EfolderDropZone
        active={dragActive}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onClick={() => inputRef.current?.click()}
      >
        <FileZip size={36} weight="regular" style={{ marginBottom: 8, opacity: 0.6 }} aria-hidden />
        <DropZoneTitle>{dragActive ? "Drop zips here" : "Drag & drop eFolder zips"}</DropZoneTitle>
        <DropZoneText>
          or click to browse — any number of .zip downloads. No need to pick a matter: each zip is
          matched to its veteran after it arrives.
        </DropZoneText>
      </EfolderDropZone>

      {state.rejected > 0 && (
        <MutedText>
          {plural(state.rejected, "file")} skipped because {state.rejected === 1 ? "it is" : "they are"} not a zip.
        </MutedText>
      )}
      {uploading && <MutedText>Keep this tab open until every zip has been sent.</MutedText>}

      <EfolderHeader>
        <EfolderTitle data-testid="efolder-review-count">
          Needs review
          <TabBadge active={needsReview.length > 0}>{needsReview.length}</TabBadge>
        </EfolderTitle>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {autoWaitingBatches.map((batchId) => (
            <SmallBtn $primary type="button" key={batchId} onClick={() => handleConfirmAll(batchId)}>
              Confirm all auto-matched ({autoWaiting.filter((row) => row.batchId === batchId).length})
            </SmallBtn>
          ))}
          <SmallBtn type="button" onClick={loadList}>Refresh</SmallBtn>
        </div>
      </EfolderHeader>

      {state.notice && (
        <MutedText role="status">
          {state.notice}{" "}
          <button type="button" onClick={() => dispatch({ type: "notice", notice: null })} style={{ border: "none", background: "none", cursor: "pointer", color: "inherit", textDecoration: "underline" }}>
            dismiss
          </button>
        </MutedText>
      )}
      {state.loadError && <ErrorNotice role="alert">{state.loadError}</ErrorNotice>}

      {sessionRows.length > 0 && renderTable(sessionRows, "efolder-session-table")}

      {otherRows.length > 0 && (
        <>
          <MutedText>Waiting for review and your recent uploads</MutedText>
          {renderTable(otherRows, "efolder-recent-table")}
        </>
      )}

      {state.loading && rows.length === 0 && <MutedText>Loading…</MutedText>}
      {!state.loading && rows.length === 0 && !state.loadError && (
        <MutedText>No eFolder zips yet. Drop one above to start.</MutedText>
      )}

      {state.modal && (
        <EfolderConfirmModal
          job={state.modal.job}
          mode={state.modal.mode}
          onClose={() => dispatch({ type: "modal/close" })}
          onDone={(payload, info) => {
            setJobFromResult(payload);
            dispatch({ type: "modal/close" });
            if (info?.conflict) dispatch({ type: "notice", notice: info.conflict });
          }}
        />
      )}
    </EfolderWrapper>
  );
};

export default EfolderUploadView;
